import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog } from "@mui/material";
import { useMemo } from "react";
import { toast } from "react-toastify";

import cashImg from "../../assets/imgs/exchange/money.png";
import logoImg from "../../assets/imgs/garage/repair.png";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import { CONFIRMATION_DELAY } from "../../constants/const";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  approveGameCashAction,
  carRepairAction,
  getGarageListAction,
} from "../../reducers/game.slice";
import { updateCashBalance } from "../../reducers/profile.slice";
import { CarInventoryType } from "../../types/CarInventoryType";
import { toUSDFormat } from "../../utils/utils";

import useStyles from "./GarageRepairStyles";

interface GarageRepairPopupProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  targetItem: CarInventoryType | null;
}

const GarageRepairPopup: React.FC<GarageRepairPopupProps> = ({
  openModal,
  setOpenModal,
  targetItem,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { account } = useWallet();
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance, balance } = useGameBankBalance(
    account,
    signMsg,
    signature
  );
  const { myProfile } = useAppSelector((state) => state.profile);

  const handleClose = () => {
    setOpenModal(false);
  };

  const repairCost = useMemo(() => {
    if (!targetItem || targetItem.id === undefined) return 0;

    return (
      ((targetItem.carType.basePrice * targetItem.car.damagePercent) / 100) * 5
    );
  }, [targetItem]);

  const handleRepairCar = async () => {
    if (!targetItem || targetItem.id === undefined) return;

    if (balance < repairCost) {
      toast.info("Insufficient cash balance!");
      return;
    }

    const action = () => {
      dispatchTxAction(
        dispatch,
        carRepairAction({
          itemId: targetItem?.id,
          account,
        }),
        () => {
          setOpenModal(false);
          updateBalance(account, signMsg, signature);
          dispatch(updateCashBalance(account));
          toast.success("Successfully repaired a car.");

          if (myProfile.city === undefined) return;
          dispatch(getGarageListAction({ account }));
        },
        CONFIRMATION_DELAY
      );
    };

    const allowance = await getGameBankAllowance(
      account,
      config.inventoryAddress
    );

    if (repairCost > allowance) {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({
          cashAmount: 10 ** 18,
          account,
          spender: config.inventoryAddress,
        }),
        () => {
          action();
        },
        CONFIRMATION_DELAY * 2
      );
    } else {
      action();
    }
  };

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.body}>
      <Box className={classes.modalContent}>
        <Box className={classes.closeIconBody}>
          <Button onClick={handleClose}>
            <CloseIcon htmlColor="gray" />
          </Button>
        </Box>

        <Box className={classes.contentBody}>
          <Box component={"img"} src={logoImg} className={classes.logoImg} />
          <Box className={classes.titleText}>Repair</Box>
          <Box>{targetItem?.carType.carName}</Box>
          <Box color="gray">
            {targetItem?.car.damagePercent}% damage - $
            {toUSDFormat(targetItem?.car.value || 0)}
          </Box>
          <Box
            component={"img"}
            src={`/assets/imgs/cars/${targetItem?.carType.image}.png`}
            className={classes.carImg}
          />
          <Box className={classes.carDetail}>
            <Box>Repair cost: </Box>
            <Box component={"img"} src={cashImg} />
            <Box>$ {toUSDFormat(repairCost)}</Box>
          </Box>
          <Box className={classes.buttonGroup}>
            <Button variant="contained" onClick={handleRepairCar}>
              Confirm
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default GarageRepairPopup;
